import Vue from "vue";
import axios, { AxiosError } from "axios";
import VueAxios from "vue-axios";
import { Store } from "vuex";
import VueRouter from "vue-router";
import { ApiErrors, ApiErrorsRuleBroken } from "@/shared/models/apiErrors";
import { AuthSuccess } from "@/store/modules/auth";
import { DateTime } from "luxon";
import { BusinessUnit } from "@/shared/models/businessUnit";

const api = axios.create({
  baseURL: `/api`,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
});

Vue.use(VueAxios, api);

export function setupUseInterceptors(
  store: Store<any>,
  router: VueRouter
): void {
  api.interceptors.request.use((config) => {
    const token = store.getters["auth/accessToken"];
    const activeBusinessUnit = store.getters["businessUnit/activeBusinessUnit"] as BusinessUnit | null | undefined;
    config.headers.Authorization = `Bearer ${token}`;
    config.headers["accept-language"] = store.getters["language/language"];

    // add users local time to every axios request
    config.params = config.params ?? {};
    config.params['usersLocalTime'] = DateTime.local().toISO();

    config.headers['local-time'] = DateTime.local().toISO();
    config.headers['active-business-unit-id'] = activeBusinessUnit?.id;

    return config;
  });

  api.interceptors.response.use(
    (response) => {
      if (response.status === 200 || response.status === 201) {
        return Promise.resolve(response);
      } else {
        return Promise.reject(response);
      }
    },
    (error: AxiosError<any>) => {
      if (error.response && error.response.status) {
        switch (error.response.status) {
          case 400: {
            if (error.response.data instanceof Blob) {
              const data = error.response.data as Blob
              data.text().then(x => {
                const parsed = JSON.parse(x) as ApiErrors;
                if (parsed.code === "RuleBroken") {
                  const brokenRules =
                    (parsed as ApiErrorsRuleBroken).brokenRules || [];
                  brokenRules
                    .filter(x => x.name !== "HandleRefreshToken.InvalidToken")
                    .forEach((x) =>
                      store.dispatch("showNotification", {
                        color: "error",
                        message: x.name,
                        timeout: 5000,
                        localize: true,
                      })
                    );
                } else {
                  store.dispatch("showNotification", {
                    color: "error",
                    message: "Error.400",
                    timeout: 5000,
                    localize: true,
                  });
                }
              });
            }
            else {
              const data = error.response.data as ApiErrors;
              if (data.code === "RuleBroken") {
                const brokenRules =
                  (data as ApiErrorsRuleBroken).brokenRules || [];
                brokenRules
                  .filter(x => x.name !== "HandleRefreshToken.InvalidToken")
                  .forEach((x) =>
                    store.dispatch("showNotification", {
                      color: "error",
                      message: x.name,
                      timeout: 5000,
                      localize: true,
                    })
                  );
              } else if (data.code === "Warning") {
                const warnings =
                  (data as ApiErrorsRuleBroken).warnings || [];
                warnings
                  .filter(x => x.name !== "HandleRefreshToken.InvalidToken")
                  .forEach((x) =>
                    store.dispatch("showNotification", {
                      color: "warning",
                      message: x.name,
                      timeout: 5000,
                      localize: true,
                    })
                  );
              } else {
                store.dispatch("showNotification", {
                  color: "error",
                  message: "Error.400",
                  timeout: 5000,
                  localize: true,
                });
              }
            }
            break;
          }
          case 401:
            return store.dispatch("auth/refresh", false).then((res: AuthSuccess) => {
              if (res.isSignedIn) {
                const token = store.getters["auth/accessToken"];
                error.config.headers.Authorization = `Bearer ${token}`;
                return api.request(error.config);
              } else {
                if (router.currentRoute.name !== "Signin") {
                  router.push({ name: "Signin" });
                }
                return Promise.reject(error.response);
              }
            });
          case 403:
            router.replace({
              path: "/signin",
              query: { redirect: router.currentRoute.fullPath },
            });
            break;
          case 404:
            store.dispatch("showNotification", {
              color: "error",
              message: "Error.404",
              timeout: 5000,
              localize: true,
            });
            break;
          case 405:
            store.dispatch("showNotification", {
              color: "error",
              message: "Error.405",
              timeout: 5000,
              localize: true,
            });
            break;
          case 500: {
            store.dispatch("showNotification", {
              color: "error",
              message: "Error.500",
              timeout: 5000,
              localize: true,
            });
            break;
          }
        }
        return Promise.reject(error.response);
      } else {
        store.dispatch("showNotification", {
          color: "error",
          message: "Could not reach server",
          timeout: 5000,
          localize: true,
        });
        return Promise.reject(null);
      }
    }
  );
}
